import {
  Box,
  ExpansionPanelProps,
  HistoryIcon,
  InfoCardList,
  InfoCardProps,
  SidebarExpansionPanel,
  Typography,
} from '@forgeglobal/design-framework'
import { OrderType, useTransactionHistoryQuery } from 'graphql/bindings/AdminBindings'
import { formatAbbreviatedMoney, formatSharePrice } from 'util/NumberUtils'
import React, { Fragment } from 'react'
import capitalize from 'lodash/capitalize'
import moment from 'moment'
import numeral from 'numeral'

interface TransactionHistorySidebarPanelProps extends Omit<ExpansionPanelProps, 'children'> {
  accountId: string
}

const TransactionHistorySidebarPanel: React.FC<TransactionHistorySidebarPanelProps> = ({ accountId, ...props }) => {
  const { data } = useTransactionHistoryQuery({ variables: { accountId: accountId } })
  const deals = data?.account.deals ?? []
  const dataList = deals.map<InfoCardProps>(deal => {
    const volume = deal.orderType === OrderType.Ask ? deal.totalSellSideVolume : deal.totalBuySideVolume
    const revenue = deal.orderType === OrderType.Ask ? deal.totalSellSideRevenue : deal.totalBuySideRevenue
    const takeRate = revenue / volume
    const date = moment(deal.dateFullyAccrued).format('M/D/YY')
    const formattedRev = revenue ? formatAbbreviatedMoney(revenue) : 'No'
    return {
      logo: deal.company.logo_url,
      leftTitle: deal.company.name,
      leftSubtitle: capitalize(deal.orderType === OrderType.Ask ? 'Sell' : 'Buy'),
      rightTitle: formatAbbreviatedMoney(volume) || '–',
      rightSubtitle: deal.avgSharePrice ? `${formatSharePrice(deal.avgSharePrice)} gross PPS` : '–',
      notes: (
        <Fragment>
          <div>Closed on {date}</div>
          {deal.orderCount > 1 && <div>{deal.orderCount} orders</div>}
          <div>{numeral(deal.totalNumShares).format('0,0')} shares</div>
          <div>{formattedRev} net revenue</div>
          <div>Take rate {numeral(takeRate).format('0.00%')}</div>
        </Fragment>
      ),
    }
  })

  return (
    <SidebarExpansionPanel icon={<HistoryIcon />} title="Transaction History" {...props}>
      {!!dataList.length && <InfoCardList items={dataList} />}
      {dataList.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
          <Typography color="primary" variant="overline">
            No historical transactions
          </Typography>
        </Box>
      )}
    </SidebarExpansionPanel>
  )
}

export { TransactionHistorySidebarPanel }
